import { haptic } from "@/lib/haptics";
import { useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const monthLabel = (d) => d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
export const iso = (d) => d.toLocaleDateString("en-CA");

export function monthRange(month) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first); start.setDate(1 - first.getDay());
  const end = new Date(start); end.setDate(start.getDate() + 41);
  return { start, end };
}

export function MonthCalendar({ month, onMonthChange, renderDay, renderDots, onDayClick, compact, minMonth }) {
  const atMin = minMonth && month <= minMonth;
  const wheelAcc = useRef(0);
  const touchX = useRef(null);
  const onWheel = (e) => {
    wheelAcc.current += e.deltaY;
    if (Math.abs(wheelAcc.current) < 450) return;
    const dir = wheelAcc.current > 0 ? 1 : -1; wheelAcc.current = 0;
    if (dir < 0 && atMin) return;
    onMonthChange(new Date(month.getFullYear(), month.getMonth() + dir, 1));
  };
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current; touchX.current = null;
    if (Math.abs(dx) < 60) return;
    const dir = dx < 0 ? 1 : -1;
    if (dir < 0 && atMin) return;
    haptic();
    onMonthChange(new Date(month.getFullYear(), month.getMonth() + dir, 1));
  };
  const { start } = monthRange(month);
  const days = useMemo(() => Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; }), [start]);
  const today = iso(new Date());
  return (
    <div className="card overflow-hidden" data-testid="month-calendar" onWheel={onWheel} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="flex items-center justify-between px-2 sm:px-4 py-2 sm:py-3 border-b border-[#F2E8E5]">
        <Button variant="ghost" size="icon" data-testid="calendar-prev" disabled={!!atMin} className={`h-11 w-11 ${atMin ? "opacity-30" : ""}`} onClick={() => { haptic(); onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1)); }}><ChevronLeft className="h-5 w-5" /></Button>
        <div className="flex items-center gap-3">
          <h2 className="font-serif text-lg sm:text-xl font-semibold" data-testid="calendar-month-label">{monthLabel(month)}</h2>
          <Button variant="outline" size="sm" className="rounded-full h-7 text-xs" data-testid="calendar-today" onClick={() => onMonthChange(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>Today</Button>
        </div>
        <Button variant="ghost" size="icon" className="h-11 w-11" data-testid="calendar-next" onClick={() => { haptic(); onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1)); }}><ChevronRight className="h-5 w-5" /></Button>
      </div>
      <div className="grid grid-cols-7 border-b border-[#F2E8E5] bg-[#FAF8F6]">{DOW.map((d) => <div key={d} className="label-xs text-center py-2">{d}</div>)}</div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const k = iso(d); const inMonth = d.getMonth() === month.getMonth();
          return (
            <div key={k} data-testid={`calendar-day-${k}`} onClick={() => onDayClick?.(k)}
              className={`border-b border-r border-[#F7EFEC] overflow-hidden ${compact ? "h-[56px] sm:h-auto sm:min-h-[96px]" : "h-[72px] sm:h-auto sm:min-h-[120px]"} p-1 sm:p-1.5 ${inMonth ? "bg-white" : "bg-[#FCFAF9] text-[#C4C4C4]"} ${onDayClick ? "cursor-pointer active:bg-[#FDF2F5] hover:bg-[#FDF2F5]/60" : ""} transition-colors ${i % 7 === 6 ? "border-r-0" : ""}`}>
              <div className={`text-xs font-semibold mb-1 h-6 w-6 flex items-center justify-center rounded-full mx-auto sm:mx-0 ${k === today ? "bg-[#C86D82] text-white" : ""}`}>{d.getDate()}</div>
              {renderDots ? <><div className="sm:hidden flex flex-wrap justify-center gap-1 px-0.5">{renderDots(k, inMonth)}</div><div className="hidden sm:block space-y-0.5">{renderDay(k, inMonth)}</div></> : <div className="space-y-0.5">{renderDay(k, inMonth)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
